"use client"

import { useState, useEffect } from "react"
import { getDatabase, ref, set, push, get, ref as dbRef, onValue,runTransaction } from "firebase/database"
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useCurrentUid } from "../hooks/useCurrentUid";

const getIndianTimestamp = () => {
  const now = new Date()
  const istOffset = 5.5 * 60 * 60 * 1000
  const istTime = new Date(now.getTime() + istOffset)
  return istTime.toISOString().replace("T", " ").replace("Z", "")
}

const getTodayDateKey = () => {
  const now = new Date()
  const istOffset = 5.5 * 60 * 60 * 1000
  const istDate = new Date(now.getTime() + istOffset)
  istDate.setHours(0, 0, 0, 0)           // start of day
  return istDate.toISOString().split('T')[0]  // "2025-03-05"
}
const sendNotification = async (userId, quoteId, brand, companyName, category) => {
  const db = getDatabase()
  const notificationRef = ref(db, `supplier_notifications/Quotations/${userId}/${quoteId}`)
  const newNotification = {
    brand,
    category,
    title: "New Quotation",
    message: `A new quote request has been sent by ${companyName} for ${brand} (${category}). Please review the query details.`,
    gifUrl: "/Images/New notification.gif",
    timestamp: getIndianTimestamp(),
    seen: false,
  }

  try {
    await set(notificationRef, newNotification)
  } catch (error) {
    console.error("Error sending notification:", error)
  }
}

export default function QuoteRequestForm() {
  const userId = useCurrentUid()
  const [selectedCategory, setSelectedCategory] = useState("cement")
  const [selectedBrands, setSelectedBrands] = useState([])
  const [grade, setGrade] = useState("")
  const [quantity, setQuantity] = useState("")
  const [quantityUnit, setQuantityUnit] = useState("bags")
  const [siteAddress, setSiteAddress] = useState("")
  const [contactInfo, setContactInfo] = useState({ name: "", phone: "" })
  const [companyName, setCompanyName] = useState("Not Specified")
  const [status, setStatus] = useState("")
  const [success, setSuccess] = useState(false)
  const [formErrors, setFormErrors] = useState({})
  const [loading, setLoading] = useState(true)
  const [sessionError, setSessionError] = useState("")
  const [currentUser, setCurrentUser] = useState(null)
const MAX_QUOTES_PER_DAY = 10

const [dailyCount, setDailyCount] = useState(0)
const [remainingQuotes, setRemainingQuotes] = useState(10)
  // Category specific state
  const [cementType, setCementType] = useState("bags")
  const [steelGrade, setSteelGrade] = useState("")
  const [steelType, setSteelType] = useState("")
  const [machineryType, setMachineryType] = useState("")
  const [machineryCapacity, setMachineryCapacity] = useState("")
  const [machineryCondition, setMachineryCondition] = useState("new")

  const cementBrands = ["Ultratech", "Shree", "JSW", "ACC", "JKSuper", "Ambuja"]
  const cementGrades = ["OPC43", "OPC53", "PPC", "PSC"]
  const steelBrands = ["TATA Steel", "JSW Steel", "SAIL", "Jindal Steel", "Essar Steel"]
  const steelGrades = ["Fe 415", "Fe 500", "Fe 550", "Fe 600"]
  const steelTypes = ["TMT Bars", "Structural Steel", "Hot Rolled", "Cold Rolled", "Galvanized"]
  const machineryBrands = ["JCB", "Caterpillar", "TATA Hitachi", "Komatsu", "L&T", "BEML"]
  const machineryTypes = ["Excavator", "Loader", "Concrete Mixer", "Crane", "Bulldozer", "Roller"]

  const getBrands = () => {
    switch (selectedCategory) {
      case "cement":
        return cementBrands
      case "steel":
        return steelBrands
      case "machinery":
        return machineryBrands
      default:
        return cementBrands
    }
  }

  useEffect(() => {
  if (!userId) return

  const today = getTodayDateKey()
  const db = getDatabase()
  const countRef = ref(db, `limit_counter/daily_quote_counts/${userId}/${today}`)

  const unsubscribe = onValue(countRef, (snapshot) => {
    const count = snapshot.val() || 0
    setDailyCount(count)
    setRemainingQuotes(Math.max(0, MAX_QUOTES_PER_DAY - count))
  })

  return () => unsubscribe()
}, [userId])
  const auth = getAuth();

  useEffect(() => {
    const connectedRef = dbRef(getDatabase(), ".info/connected");

    // Offline check
    if (!navigator.onLine) {
      setSessionError("You are offline. Please check your internet connection.");
      setLoading(false);
      return;
    }

    // Auth state check
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setSessionError("Session expired. Please login again.");
        setLoading(false);
        return;
      }

      setCurrentUser(user);
      setSessionError("");

      // Fetch profile data from profileDashboard
      try {
        const db = getDatabase();
        const profileRef = ref(db, `profileDashboard/${userId}`);
        const snapshot = await get(profileRef);
        if (snapshot.exists()) {
          const profileData = snapshot.val();
          setContactInfo({
            name: profileData.username || "",
            phone: profileData.phone || ""
          });
          setCompanyName(profileData.company_name || "Not Specified");
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
        setContactInfo({ name: "", phone: "" });
        setCompanyName("Not Specified");
      }

      // Firebase connection check
      onValue(connectedRef, (snap) => {
        if (snap.val() === false) {
          setSessionError("Session expired. Please login again.");
          setLoading(false);
          return;
        }

        setSessionError("");
        setLoading(false);
      });
    });

    return () => unsubscribe();
  }, []);

  // Reset form fields when category changes
  useEffect(() => {
    setSelectedBrands([])
    setGrade("")
    setQuantity("")
    setFormErrors({})

    if (selectedCategory === "cement") {
      setCementType("bags")
      setQuantityUnit("bags")
    } else if (selectedCategory === "steel") {
      setSteelGrade("")
      setSteelType("")
      setQuantityUnit("tons")
    } else if (selectedCategory === "machinery") {
      setMachineryType("")
      setMachineryCapacity("")
      setMachineryCondition("new")
      setQuantityUnit("units")
    }
  }, [selectedCategory])

  const handleBrandChange = (brand) => {
    const brands = getBrands()
    if (brand === "All") {
      setSelectedBrands((prev) => (prev.length === brands.length ? [] : brands))
    } else {
      setSelectedBrands((prev) => {
        if (prev.includes(brand)) {
          return prev.filter((b) => b !== brand)
        } else {
          return [...prev, brand]
        }
      })
    }
    if (formErrors.brands) {
      setFormErrors((prev) => ({ ...prev, brands: null }))
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!selectedBrands.length) {
      errors.brands = "Please select at least one brand"
    }

    if (!siteAddress || siteAddress.trim().length < 5) {
  errors.siteAddress = "Site address must be at least 5 characters long";
} else if (siteAddress.trim().length > 200) {
  errors.siteAddress = "Site address cannot exceed 200 characters";
}
    

    if (!contactInfo.name.trim()) {
      errors.name = "Name is required"
    }

    if (!contactInfo.phone.trim()) {
      errors.phone = "Phone number is required"
    } else if (!/^\d{10,15}$/.test(contactInfo.phone)) {
      errors.phone = "Phone number must be 10-15 digits"
    }

    if (selectedCategory === "cement") {
      if (!grade) errors.grade = "Please select a cement grade"
      if (!quantity) errors.quantity = "Quantity is required"
    }

    if (selectedCategory === "steel") {
      if (!steelGrade) errors.steelGrade = "Please select a steel grade"
      if (!steelType) errors.steelType = "Please select a steel type"
      if (!quantity) errors.quantity = "Quantity is required"
    }

    if (selectedCategory === "machinery") {
      if (!machineryType) errors.machineryType = "Please select a machinery type"
      if (!quantity) errors.quantity = "Quantity is required"
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (event) => {
  event.preventDefault()

  if (!userId) {
    setStatus("No account selected. Please refresh.")
    return
  }

  if (remainingQuotes <= 0) {
    setStatus("You have reached the daily limit of 10 quote requests. Please try again tomorrow.")
    setTimeout(() => setStatus(""), 5000)
    return
  }

  if (!validateForm()) {
    setStatus("Please fill all required fields.")
    setTimeout(() => setStatus(""), 3000)
    return
  }

  if (sessionError) {
    setStatus(sessionError)
    return
  }

  try {
    const db = getDatabase()
    const timestamp = getIndianTimestamp()
    const today = getTodayDateKey()

    // We'll collect all promises and run transaction last
    const writePromises = []

    for (const brand of selectedBrands) {
      const requestData = {
        category: selectedCategory,
        brand,
        siteAddress,
        contactInfo,
        companyName,
        timestamp,
        quantity,
        quantityUnit,
      }

      if (selectedCategory === "cement") {
        requestData.grade = grade
        requestData.cementType = cementType
      } else if (selectedCategory === "steel") {
        requestData.steelGrade = steelGrade
        requestData.steelType = steelType
      } else if (selectedCategory === "machinery") {
        requestData.machineryType = machineryType
        requestData.machineryCapacity = machineryCapacity
        requestData.machineryCondition = machineryCondition
      }

      const requestsRef = ref(db, `quoteRequests/${userId}`)
      const newRequestRef = push(requestsRef) // generates key locally

      writePromises.push(
        set(newRequestRef, requestData)
      )

      writePromises.push(
        sendNotification(userId, newRequestRef.key, brand, companyName, selectedCategory)
      )
    }

    // Now increment counter atomically
    const countRef = ref(db, `limit_counter/daily_quote_counts/${userId}/${today}`)

    await runTransaction(countRef, (current) => {
      const count = current || 0
      if (count >= MAX_QUOTES_PER_DAY) {
        throw new Error("daily_limit_reached")
      }
      return count + selectedBrands.length   // ← important: + number of brands!
    })

    // If transaction succeeds → write all quote requests
    await Promise.all(writePromises)

    // Reset form
    setSelectedBrands([])
    setGrade("")
    setQuantity("")
    setSiteAddress("")
    setContactInfo({ name: "", phone: "" })
    setCementType("bags")
    setSteelGrade("")
    setSteelType("")
    setMachineryType("")
    setMachineryCapacity("")
    setMachineryCondition("new")
    setFormErrors({})

    setSuccess(true)
    setTimeout(() => setSuccess(false), 3000)
  } catch (error) {
    console.error(error)
    if (error.message === "daily_limit_reached") {
      setStatus("Daily limit of 10 quote requests reached. Try again tomorrow.")
    } else {
      setStatus("Error submitting request: " + error.message)
    }
    setTimeout(() => setStatus(""), 5000)
  }
}

  if (loading) {
    return <p>Loading...</p>;
  }

  if (sessionError) {
    return <p style={{ color: "red" }}>{sessionError}</p>;
  }

  return (
    <div className="quote-request-form">
      <div className="quote-request-form__header">
        <h3 className="quote-request-form__title">Request a Quote</h3>
      </div>

      <div className="select-toggle">
        <div className="form-group">
          <label className="quote-form-label">Select Category</label>
          <div className="toggle-container">
            <button
              className={`toggle-button ${selectedCategory === "cement" ? "toggle-button--active" : ""}`}
              onClick={() => setSelectedCategory("cement")}
            >
              Cement
            </button>
            <button
              className={`toggle-button ${selectedCategory === "steel" ? "toggle-button--active" : ""}`}
              onClick={() => setSelectedCategory("steel")}
            >
              Steel
            </button>
            <button
              className={`toggle-button ${selectedCategory === "machinery" ? "toggle-button--active" : ""}`}
              onClick={() => setSelectedCategory("machinery")}
            >
              Machinery
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="quote-form">
        {/* Category specific forms */}
        {selectedCategory === "cement" && (
          <>
            <div className="form-group">
              <label className="quote-form-label">Cement Type</label>
              <div className="radio-buttons">
                <button
                  type="button"
                  className={`styled-button ${cementType === "bags" ? "styled-button--active" : ""}`}
                  onClick={() => {
                    setCementType("bags")
                    setQuantityUnit("bags")
                  }}
                >
                  Bags
                </button>
                <button
                  type="button"
                  className={`styled-button ${cementType === "bulker" ? "styled-button--active" : ""}`}
                  onClick={() => {
                    setCementType("bulker")
                    setQuantityUnit("MT")
                  }}
                >
                  Bulker
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="quote-form-label">Grades</label>
              <div className="checkbox-grid">
                {cementGrades.map((g) => (
                  <button
                    key={g}
                    type="button"
                    className={`styled-button ${grade === g ? "styled-button--active" : ""}`}
                    onClick={() => setGrade(g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
              {formErrors.grade && <div className="error-message">{formErrors.grade}</div>}
            </div>

            <div className="form-group">
              <label className="quote-form-label">Brands</label>
              <div className="checkbox-grid">
                <button
                  type="button"
                  className={`styled-button ${selectedBrands.length === cementBrands.length ? "styled-button--active" : ""}`}
                  onClick={() => handleBrandChange("All")}
                >
                  All Brands
                </button>
                {cementBrands.map((b) => (
                  <button
                    key={b}
                    type="button"
                    className={`styled-button ${selectedBrands.includes(b) ? "styled-button--active" : ""}`}
                    onClick={() => handleBrandChange(b)}
                  >
                    {b}
                  </button>
                ))}
              </div>
              {formErrors.brands && <div className="error-message">{formErrors.brands}</div>}
            </div>
          </>
        )}

        {selectedCategory === "steel" && (
          <>
            <div className="form-group">
              <label className="quote-form-label">Steel Grade</label>
              <div className="checkbox-grid">
                {steelGrades.map((g) => (
                  <button
                    key={g}
                    type="button"
                    className={`styled-button ${steelGrade === g ? "styled-button--active" : ""}`}
                    onClick={() => setSteelGrade(g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
              {formErrors.steelGrade && <div className="error-message">{formErrors.steelGrade}</div>}
            </div>

            <div className="form-group">
              <label className="quote-form-label">Steel Type</label>
              <div className="checkbox-grid">
                {steelTypes.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`styled-button ${steelType === t ? "styled-button--active" : ""}`}
                    onClick={() => setSteelType(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {formErrors.steelType && <div className="error-message">{formErrors.steelType}</div>}
            </div>

            <div className="form-group">
              <label className="quote-form-label">Brands</label>
              <div className="checkbox-grid">
                <button
                  type="button"
                  className={`styled-button ${selectedBrands.length === steelBrands.length ? "styled-button--active" : ""}`}
                  onClick={() => handleBrandChange("All")}
                >
                  All Brands
                </button>
                {steelBrands.map((b) => (
                  <button
                    key={b}
                    type="button"
                    className={`styled-button ${selectedBrands.includes(b) ? "styled-button--active" : ""}`}
                    onClick={() => handleBrandChange(b)}
                  >
                    {b}
                  </button>
                ))}
              </div>
              {formErrors.brands && <div className="error-message">{formErrors.brands}</div>}
            </div>
          </>
        )}

        {selectedCategory === "machinery" && (
          <>
            <div className="form-group">
              <label className="quote-form-label">Machinery Type</label>
              <div className="checkbox-grid">
                {machineryTypes.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`styled-button ${machineryType === t ? "styled-button--active" : ""}`}
                    onClick={() => setMachineryType(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {formErrors.machineryType && <div className="error-message">{formErrors.machineryType}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="machinery-capacity" className="quote-form-label">
                Capacity
              </label>
              <input
                id="machinery-capacity"
                value={machineryCapacity}
                onChange={(e) => setMachineryCapacity(e.target.value)}
                placeholder="Enter capacity (e.g., 5 tons, 100 HP)"
                className="form-control"
                maxLength={50}
              />
            </div>

            <div className="form-group">
              <label className="quote-form-label">Condition</label>
              <div className="radio-buttons">
                <button
                  type="button"
                  className={`styled-button ${machineryCondition === "new" ? "styled-button--active" : ""}`}
                  onClick={() => setMachineryCondition("new")}
                >
                  New
                </button>
                <button
                  type="button"
                  className={`styled-button ${machineryCondition === "used" ? "styled-button--active" : ""}`}
                  onClick={() => setMachineryCondition("used")}
                >
                  Used
                </button>
                <button
                  type="button"
                  className={`styled-button ${machineryCondition === "refurbished" ? "styled-button--active" : ""}`}
                  onClick={() => setMachineryCondition("refurbished")}
                >
                  Refurbished
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="quote-form-label">Brands</label>
              <div className="checkbox-grid">
                <button
                  type="button"
                  className={`styled-button ${selectedBrands.length === machineryBrands.length ? "styled-button--active" : ""}`}
                  onClick={() => handleBrandChange("All")}
                >
                  All Brands
                </button>
                {machineryBrands.map((b) => (
                  <button
                    key={b}
                    type="button"
                    className={`styled-button ${selectedBrands.includes(b) ? "styled-button--active" : ""}`}
                    onClick={() => handleBrandChange(b)}
                  >
                    {b}
                  </button>
                ))}
              </div>
              {formErrors.brands && <div className="error-message">{formErrors.brands}</div>}
            </div>
          </>
        )}

        {/* Common fields */}
        <div className="form-group">
          <label htmlFor="quantity" className="quote-form-label">
            Quantity ({quantityUnit})
          </label>
          <input
            id="quantity"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            onWheel={(e) => e.target.blur()}
            placeholder={`Number of ${quantityUnit}`}
            className="form-control"
            required
          />
          {formErrors.quantity && <div className="error-message">{formErrors.quantity}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="site-address" className="quote-form-label">
            Site Address
          </label>
          <textarea
            id="site-address"
            value={siteAddress}
            onChange={(e) => setSiteAddress(e.target.value)}
            placeholder="Enter the delivery address"
            className="form-control"
            required
          />
          {formErrors.siteAddress && <div className="error-message">{formErrors.siteAddress}</div>}
           <p className="char-count">{siteAddress.replace(/\s/g, "").length} / 200</p>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="contact-name" className="quote-form-label">
              Contact Name
            </label>
            <input
              id="contact-name"
              value={contactInfo.name}
              onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
              className="form-control"
              required
            />
            {formErrors.name && <div className="error-message">{formErrors.name}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="company-name" className="quote-form-label">
              Company Name
            </label>
            <input
              id="company-name"
              value={companyName}
              className="form-control"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="phone" className="quote-form-label">
            Phone
          </label>
          <input
            id="phone"
            value={contactInfo.phone}
            onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
            className="form-control"
            minLength={10}
            maxLength={15}
            required
          />
          {formErrors.phone && <div className="error-message">{formErrors.phone}</div>}
        </div>

       <button 
  type="submit" 
  className="button button--primary button--full "
  disabled={remainingQuotes <= 0 || selectedBrands.length === 0}
>
  Submit Quote Request
</button>
{success && (
          <div className="alert alert--success">
            <p style={{ textAlign: "center" }}>Quote request submitted successfully!</p>
          </div>
        )}
        
        <div className="daily-limit-info">
  {remainingQuotes > 0 ? (
    <p>You have <strong>{remainingQuotes}</strong> quote requests left today</p>
  ) : (
    <p style={{ color: "#d32f2f", fontWeight: "500" }}>
      Daily limit reached (10/day). Please come back tomorrow.
    </p>
  )}
</div>

        {status && (
          <div className="alert alert--error">
            <p>{status}</p>
          </div>
        )}
      </form>
    </div>
  )
}